import {
  type CanvasElementFrom,
  type IoConfig,
  type PropsOf,
  type RowClickActionProps,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  type ElementLabelProps,
  type ElementSizingMode,
  type ElementTitleStyleProps,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputCustomTemplateModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
} from '@blaze-shared/elements-domain';
import { DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { HtmlCardComponent } from './html-card.component';
import { HtmlCardPropsBuilder } from './html-card.props-builder';
import { HtmlCardPropsExtractor } from './html-card.props-extractor';

export type HtmlCardDef = WithProps<HtmlCardPropsModel> & WithIoConfig;
export type HtmlCardElement = CanvasElementFrom<'htmlCard'>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    htmlCard: HtmlCardDef;
  }
}

const DEFAULT_WIDTH = 500;
const DEFAULT_HEIGHT = 300;
const MIN_SIZE = 48;
const MAX_WIDTH = 1920;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed'];
const DATA_SOURCE_NOT_BOUND_MESSAGE = 'The Value of the element is not bound';

registerElementDefinition({
  type: 'htmlCard',
  componentType: HtmlCardComponent,
  isListed: true,
  availableFor: 'dev',
  name: 'HTML Card',
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: HtmlCardPropsBuilder,
  propsExtractor: HtmlCardPropsExtractor,
  defaults: getDefaults(),
  tags: ['html', 'template', 'handlebars', 'custom', 'card'],
  groups: ['advanced'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  features: {
    visualStyle: 'block',
  },
  version: 1,
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'action', schema: DEFAULT_SCHEMA }],
  };
}

type PropsModel = CanvasElementPropertiesWithData &
  StyleConfigProps &
  VisibilityElementProps &
  RowClickActionProps;

export type HtmlCardPropsModel = PropsModel & {
  content: {
    htmlCard: {
      template: PropertiesConfigInputCustomTemplateModel;
    };
    title: ElementLabelProps;
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    titleBlock: ElementTitleStyleProps;
    label: {
      color: PropertiesConfigInputColorModel;
    };
    container: {
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      fill: PropertiesConfigInputColorModel;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
  };
};

function getDefaults(): PropsOf<'htmlCard'> {
  return {
    content: {
      htmlCard: {
        template: { code: '' },
      },
      title: {
        enabled: false,
        align: 'left',
        label: 'HTML Card',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      label: {
        color: { color1: '$t.title', selectorType: 'solid' },
      },
      container: {
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        outlineWidth: '$t.containerOutlineWidth',
        fill: { color1: '$t.fillExternal', selectorType: 'solid' },
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('htmlCard', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Data',
        [],
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Data source',
            sources: 'list',
            bindings: [],
          }),
        ],
        { enabledByDefault: true },
      ),
      epc.subsections.subsection('content.htmlCard', 'Template', [
        epc.fields.customTemplateField('content.htmlCard.template'),
      ]),
      epc.subsections.subsection(
        'content.rowClick',
        'Actions',
        epc.fields.containerRowClickActionsField('On click', 'Edit item'),
        {
          // Actions are dispatched against the bound data source - see ElementDataActions.onAction.
          lockWhen: (model: HtmlCardPropsModel) =>
            getValueByPath(model, 'data.?key:value.dataSource')
              ? undefined
              : DATA_SOURCE_NOT_BOUND_MESSAGE,
        },
      ),
      epc.subsections.subsection('content.htmlCard', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.general', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),

        epc.subsections.elementBackgroundSubsection('style.container', 'General'),

        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.label.color',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
